import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './QuestionKeyWordListDisplay.scss';
import { t } from '../../services/i18nService';
import QuestionKeyWord from './QuestionKeyword';
import sessionReader from '../../store/readHelpers/SessionReader';

const QuestionKeyWordList = (props) => {
    const { question, sectionId, currentScorableItem } = props;
    const zoomLMaxHeight = 455; // see mixins.scss, variable $desktop-height-L
    const wrapperRef = useRef(null);
    const [wrapperStyle, setWrapperStyle] = useState({});
    const [isFixed, setIsFixed] = useState(false);
    // need this because there is no access to the latest state inside event handlers
    const isFixedRef = useRef(isFixed);
    let questionWrapper;
    let sectionContentTopPadding = 0;

    const wrapperClassNames = [styles.keyWordListWrapper];
    if (isFixed) {
        wrapperClassNames.push(styles.keyWordListWrapperFixed);
    }

    useEffect(() => {
        if (sessionReader.isViewMediaPrintOrPdf()) {
            return null;
        }

        questionWrapper = document.querySelector('.question-wrapper.current');
        handleScroll();

        const sectionContent = document.getElementById('sectionContent');
        if (sectionContent) {
            const sectionContentStyle = window.getComputedStyle(sectionContent, null);
            sectionContentTopPadding = parseInt(sectionContentStyle.getPropertyValue('padding-top'), 10) || 0;
            addScrollHandlers(sectionContent);
            return () => {
                removeScrollHandlers(sectionContent);
            };
        }

        return null;
    }, []);

    const addScrollHandlers = (sectionContent) => {
        // for smaller resolutions there is global page scroll bar
        document.addEventListener('scroll', handleScroll);
        // for bigger resolutions there is inner scrollbar on the sectionContent level
        sectionContent.addEventListener('scroll', handleScroll);
        // re-calc the height on page resize and browser zoom change as well
        window.addEventListener('resize', handleScroll);
    };

    const removeScrollHandlers = (sectionContent) => {
        sectionContent.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
    };

    const setFixedState = (fixedState) => {
        isFixedRef.current = fixedState;
        setIsFixed(fixedState);
    };

    const handleScroll = () => {
        if (!questionWrapper) {
            return;
        }

        const pageHeight = window.innerHeight;
        const headerHeight = document.getElementById('header')?.clientHeight || 0;
        const headerOffset = (pageHeight > zoomLMaxHeight && headerHeight) || 0;
        const questionWrapperOffset = questionWrapper.getBoundingClientRect().top - headerOffset - sectionContentTopPadding;

        if (questionWrapperOffset > 0) {
            if (isFixedRef.current) {
                setFixedState(false);
            }
            updateListHeight();
        } else if (!isFixedRef.current) {
            setFixedState(true);
            setWrapperStyle({});
        }
    };

    const updateListHeight = () => {
        const keyWordsListTopOffset = wrapperRef.current.getBoundingClientRect().top;

        // see QuestionKeyWordListDisplay.scss, class keyWordListWrapperFixed
        // 5.5rem - navButtons bottom offset
        // 4.5rem - ceq header height
        // ref: https://inspera.atlassian.net/browse/TEII-4633

        setWrapperStyle({ maxHeight: `calc(100vh - ${keyWordsListTopOffset}px - 5.5rem - 4.5rem)` });
    };

    return (<div className={wrapperClassNames.join(' ')} style={wrapperStyle} ref={wrapperRef}>
        <ul>
            <li className={styles.keyWordListName}>
                { t('question.keyWordList') }
            </li>

            { question.responseModels.map((response) => {
                return (
                    <QuestionKeyWord
                        key={response.identifier}
                        question={question}
                        response={response}
                        currentScorableItem={currentScorableItem}
                        sectionId={sectionId}
                    />
                );
            })}
        </ul>
    </div>);
};

QuestionKeyWordList.propTypes = {
    question: PropTypes.object.isRequired,
    currentScorableItem: PropTypes.number,
    sectionId: PropTypes.number.isRequired,
};

export default QuestionKeyWordList;
